import { z } from 'zod';
import { nipProblem } from '../nip';
import { CLIENT_HISTORY_KINDS, CLIENT_NOTE_MAX_CHARS, type ClientHistoryKind } from './client-notes';
import {
  currencySchema,
  emailSchema,
  idSchema,
  localDateSchema,
  localTimeSchema,
  markdownSchema,
  nameSchema,
  rateSchema,
  urlSchema,
} from './common';
import { CLIENT_EDITABLE_STATUSES } from './crm';
import {
  idempotencyKeySchema,
  mcpContactVersionArg,
  mcpCursorArg,
  mcpLimitArg,
  mcpNoteArg,
  mcpQueryArg,
} from './mcp-base';
import { MAX_TRANSCRIPT_CHARS, MEETING_LIST_WHEN } from './meetings';
import { INCIDENT_STATUSES, MONITOR_LIMITS } from './monitoring';
import { OFFER_LIST_FILTERS } from './offers';
import { PROJECT_STATUSES } from './projects';
import { TASK_PRIORITIES, TASK_STATUSES } from './work';

/**
 * Codzienna praca przez MCP (Fala 10; DECISIONS 2026-10-07 [S] „Wszystko z codziennej pracy da się zlecić agentowi
 * przez MCP”): klienci, kontakty, projekty, zadania, spotkania, notatki i historia klienta, poczta (odczyt, szkic,
 * propozycja wysyłki), Dziś, czas, wiedza, oferty (odczyt), Utrzymanie i incydenty, podgląd akceptacji. BEZ ustawień
 * konta, zespołu, integracji, tokenów i sejfu. Wrażliwe działania (wysyłka maila, faktura, usunięcie, zmiana dostępów,
 * wdrożenie, stawki i warunki ofert, archiwizacja klienta) — wyłącznie jako propozycje do Akceptacji; agent nigdy ich
 * nie zatwierdza.
 *
 * Argumenty i wyniki snake_case (konwencja MCP). Każde narzędzie mutujące: `idempotency_key` i opcjonalne `note`
 * (opis prośby osoby → Historia AI). Rejestr i opisy narzędzi: `contracts/mcp.ts` (`MCP_TOOLS`), dokumentacja:
 * `docs/MCP_TOOLS.md` §3.19–§3.48, przykłady poleceń: §8.
 */

const versionArg = z.number().int().positive();
const optionalNullableText = (max: number) => z.string().trim().max(max).nullable().optional();

/** NIP w argumentach: walidacja jak w formularzu (suma kontrolna), zapis = 10 cyfr (albo numer VAT UE). */
const mcpNipArg = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .superRefine((v, ctx) => {
    // numer VAT UE (2 litery + cyfry / litery) przechodzi dalej — sprawdza go schemat klienta
    if (/^[A-Z]{2}/i.test(v) && !/^PL/i.test(v)) return;
    const problem = nipProblem(v);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  });

/** Dane firmy w argumentach MCP (adres jednym wierszem — serwer rozkłada go na pola). */
const mcpCompanyArgs = z
  .object({
    legal_name: z.string().trim().max(500).optional(),
    nip: mcpNipArg.optional(),
    regon: z.string().trim().max(20).optional(),
    krs: z.string().trim().max(20).optional(),
    /** jednym wierszem, np. „ul. Prosta 12/4, 00-838 Warszawa” */
    address: z.string().trim().max(500).optional(),
    website: urlSchema.optional(),
  })
  .strict();

const mcpContactFields = {
  name: nameSchema,
  email: emailSchema.optional(),
  phone: z.string().trim().max(40).optional(),
  role: z.string().trim().max(100).optional(),
};

// ── Klienci i kontakty ──────────────────────────────────────────────────────

export const getClientArgs = z
  .object({
    client_id: idSchema,
    /** przegląd Fali 12: zarchiwizowane kontakty (do `restore_contact`) — `contacts.list` z `includeArchived` */
    include_archived: z
      .boolean()
      .default(false)
      .describe('true = także zarchiwizowane kontakty (archived: true), np. do restore_contact'),
  })
  .strict();

export const createClientArgs = z
  .object({
    /** nazwa wyświetlana, np. „TeckWrap Europe” */
    name: nameSchema,
    status: z.enum(CLIENT_EDITABLE_STATUSES).default('active'),
    /** NIP — z `use_registry` dane firmy (nazwa prawna, adres, REGON, KRS, VAT) z rejestru (Biała lista / REGON) */
    nip: mcpNipArg.optional(),
    use_registry: z.boolean().default(true),
    company: mcpCompanyArgs.omit({ nip: true }).optional(),
    notes_md: markdownSchema.optional(),
    /** stawka domyślna netto/h — tylko osoba z „Widzi stawki i kwoty” (inaczej FORBIDDEN) */
    default_rate: rateSchema.optional(),
    currency: currencySchema.optional(),
    /** główny kontakt klienta (opcjonalnie) */
    contact: z.object(mcpContactFields).strict().optional(),
    /** ten sam NIP jest już u innego klienta: bez tej flagi = CONFLICT (nic nie powstaje) */
    allow_duplicate: z.boolean().default(false),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const updateClientArgs = z
  .object({
    client_id: idSchema,
    /** `version` z get_client (inna = CONFLICT) */
    expected_version: versionArg,
    changes: z
      .object({
        name: nameSchema.optional(),
        /** archiwizacja tylko przez propose_client_archive (akceptacja) */
        status: z.enum(CLIENT_EDITABLE_STATUSES).optional(),
        notes_md: markdownSchema.nullable().optional(),
        /** pola danych firmy do zmiany (pozostałe bez zmian) */
        company: mcpCompanyArgs.optional(),
        /** tylko z „Widzi stawki i kwoty” (inaczej FORBIDDEN) */
        default_rate: rateSchema.nullable().optional(),
        currency: currencySchema.optional(),
      })
      .strict()
      .refine((c) => Object.values(c).some((v) => v !== undefined), { message: 'Podaj co najmniej jedną zmianę' }),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const proposeClientArchiveArgs = z
  .object({
    client_id: idSchema,
    /** dlaczego archiwizować (widzi to zatwierdzający) */
    reason: z.string().trim().min(1).max(500),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const addContactArgs = z
  .object({
    client_id: idSchema,
    ...mcpContactFields,
    is_primary: z.boolean().optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const updateContactArgs = z
  .object({
    contact_id: idSchema,
    expected_version: mcpContactVersionArg,
    changes: z
      .object({
        name: nameSchema.optional(),
        email: emailSchema.nullable().optional(),
        phone: optionalNullableText(40),
        role: optionalNullableText(100),
        is_primary: z.boolean().optional(),
      })
      .strict()
      .refine((c) => Object.values(c).some((v) => v !== undefined), { message: 'Podaj co najmniej jedną zmianę' }),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Projekty ────────────────────────────────────────────────────────────────

export const MCP_PROJECT_VIEWS = ['open', 'closed', 'all'] as const;

export const listProjectsArgs = z
  .object({
    client_id: idSchema.optional(),
    /** open = planowane, aktywne i wstrzymane (domyślnie); closed = zakończone, anulowane i archiwum */
    view: z.enum(MCP_PROJECT_VIEWS).default('open'),
    status: z.array(z.enum(PROJECT_STATUSES)).min(1).max(PROJECT_STATUSES.length).optional(),
    internal: z.boolean().optional(),
    query: mcpQueryArg.optional(),
    limit: mcpLimitArg,
    cursor: mcpCursorArg,
  })
  .strict();

export const getProjectArgs = z.object({ project_id: idSchema }).strict();

export const createProjectArgs = z
  .object({
    name: nameSchema,
    /** projekt klienta; bez klienta = projekt wewnętrzny (`is_internal`) */
    client_id: idSchema.optional(),
    is_internal: z.boolean().default(false),
    goal: z.string().trim().max(2000).optional(),
    scope_md: markdownSchema.optional(),
    status: z.enum(PROJECT_STATUSES).default('active'),
    start_date: localDateSchema.optional(),
    end_date: localDateSchema.optional(),
    repo_url: urlSchema.optional(),
    figma_url: urlSchema.optional(),
    /** budżet godzin (np. 40) */
    hours_budget: z.number().positive().max(100_000).optional(),
    billable_default: z.boolean().optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.is_internal && v.client_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['client_id'], message: 'Projekt wewnętrzny nie ma klienta' });
    }
    if (v.start_date && v.end_date && v.end_date < v.start_date) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['end_date'], message: 'Koniec przed początkiem' });
    }
  });

// ── Zadania ─────────────────────────────────────────────────────────────────

export const updateTaskArgs = z
  .object({
    task_id: idSchema,
    /** `version` z list_tasks (inna = CONFLICT) */
    expected_version: versionArg,
    changes: z
      .object({
        title: z.string().trim().min(1).max(300).optional(),
        description_md: z.string().max(50_000).optional(),
        status: z.enum(TASK_STATUSES).optional(),
        priority: z.enum(TASK_PRIORITIES).optional(),
        due_date: localDateSchema.nullable().optional(),
        assignee_user_id: idSchema.nullable().optional(),
        checklist_add: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
        checklist_done: z.array(idSchema).max(50).optional(),
        checklist_undone: z.array(idSchema).max(50).optional(),
      })
      .strict()
      .refine((c) => Object.values(c).some((v) => v !== undefined), { message: 'Podaj co najmniej jedną zmianę' }),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const TASK_COMMENT_MAX = 4000;

export const addTaskCommentArgs = z
  .object({
    task_id: idSchema,
    text: z.string().trim().min(1).max(TASK_COMMENT_MAX),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Spotkania ───────────────────────────────────────────────────────────────

const mcpParticipantArg = z
  .object({
    name: nameSchema,
    email: emailSchema.optional(),
    /** osoba z zespołu */
    user_id: idSchema.optional(),
    /** kontakt klienta */
    contact_id: idSchema.optional(),
  })
  .strict();

export const listMeetingsArgs = z
  .object({
    /** jedno spotkanie ze szczegółami (notatki, podsumowanie, ustalenia, propozycje zadań) */
    meeting_id: idSchema.optional(),
    when: z.enum(MEETING_LIST_WHEN).default('upcoming'),
    client_id: idSchema.optional(),
    project_id: idSchema.optional(),
    query: mcpQueryArg.optional(),
    /** przegląd Fali 12: `meetings.list` z `archived` (bez względu na `when`) */
    archived: z
      .boolean()
      .default(false)
      .describe('true = tylko spotkania z archiwum (bez względu na when), np. do restore_meeting'),
    limit: mcpLimitArg,
  })
  .strict();

export const createMeetingArgs = z
  .object({
    title: z.string().trim().min(1).max(300),
    /** dzień i godziny w Europe/Warsaw */
    date: localDateSchema,
    start: localTimeSchema,
    end: localTimeSchema.optional(),
    client_id: idSchema.optional(),
    project_id: idSchema.optional(),
    location: z.string().trim().max(300).optional(),
    participants: z.array(mcpParticipantArg).max(50).default([]),
    notes_md: markdownSchema.optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .refine((v) => !v.end || v.end > v.start, { message: 'Koniec musi być po początku', path: ['end'] });

export const addMeetingNotesArgs = z
  .object({
    meeting_id: idSchema,
    /** dopisywane na końcu notatek spotkania (nic nie jest nadpisywane) */
    notes_md: z.string().trim().min(1).max(50_000).optional(),
    /** transkrypcja (tekst); zastępuje poprzednią */
    transcript_text: z.string().trim().min(1).max(MAX_TRANSCRIPT_CHARS).optional(),
    /** przetwórz transkrypcję / notatki: podsumowanie, ustalenia i propozycje zadań (do decyzji osoby) */
    process: z.boolean().default(false),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .refine((v) => v.notes_md !== undefined || v.transcript_text !== undefined || v.process, {
    message: 'Podaj notes_md, transcript_text albo process: true',
    path: ['notes_md'],
  });

// ── Notatki i historia klienta ──────────────────────────────────────────────

export const addClientNoteArgs = z
  .object({
    client_id: idSchema,
    body_md: z.string().trim().min(1).max(CLIENT_NOTE_MAX_CHARS),
    /** opcjonalne powiązania (ten sam klient) */
    project_id: idSchema.optional(),
    task_id: idSchema.optional(),
    meeting_id: idSchema.optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const listClientNotesArgs = z.object({ client_id: idSchema, limit: mcpLimitArg, cursor: mcpCursorArg }).strict();

export const getClientHistoryArgs = z
  .object({
    client_id: idSchema,
    /** tylko te rodzaje pozycji (domyślnie wszystkie, do których osoba ma moduł) */
    kinds: z.array(z.enum(CLIENT_HISTORY_KINDS)).min(1).max(CLIENT_HISTORY_KINDS.length).optional(),
    limit: mcpLimitArg,
    cursor: mcpCursorArg,
  })
  .strict();

// ── Poczta ──────────────────────────────────────────────────────────────────

export const listThreadsArgs = z
  .object({
    client_id: idSchema.optional(),
    /** tylko wątki bez klienta (kolejka „Przypisz do klienta”) */
    unassigned: z.boolean().default(false),
    query: mcpQueryArg.optional(),
    limit: mcpLimitArg,
    cursor: mcpCursorArg,
  })
  .strict();

export const getThreadArgs = z
  .object({
    thread_id: idSchema,
    /** najwyżej tyle ostatnich wiadomości (domyślnie 20) */
    max_messages: z.number().int().min(1).max(50).default(20),
  })
  .strict();

export const draftReplyArgs = z
  .object({
    thread_id: idSchema,
    body_text: z.string().min(1).max(50_000),
    /** wiadomość, na którą odpowiadasz (domyślnie ostatnia przychodząca) */
    reply_to_message_id: idSchema.optional(),
    /** odbiorcy inni niż domyślni (nadawca wiadomości) — odbiorca spoza kontaktów klienta wymaga potwierdzenia przy akceptacji */
    to: z.array(emailSchema).min(1).max(20).optional(),
    cc: z.array(emailSchema).max(20).optional(),
    subject: z.string().trim().min(1).max(300).optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Dziś i akceptacje ───────────────────────────────────────────────────────

export const getTodayArgs = z.object({ scope: z.enum(['mine', 'team']).default('mine') }).strict();

export const listPendingApprovalsArgs = z
  .object({
    client_id: idSchema.optional(),
    /** tylko te, które osoba tokenu może zatwierdzić w aplikacji */
    decidable_only: z.boolean().default(false),
    limit: mcpLimitArg,
  })
  .strict();

// ── Czas ────────────────────────────────────────────────────────────────────

export const updateTimeEntryArgs = z
  .object({
    entry_id: idSchema,
    expected_version: versionArg,
    changes: z
      .object({
        date: localDateSchema.optional(),
        start: localTimeSchema.optional(),
        end: localTimeSchema.optional(),
        end_date: localDateSchema.nullable().optional(),
        duration_seconds: z
          .number()
          .int()
          .positive()
          .max(24 * 3600)
          .optional(),
        description: z.string().trim().max(500).optional(),
        client_id: idSchema.nullable().optional(),
        project_id: idSchema.nullable().optional(),
        task_id: idSchema.nullable().optional(),
        billable: z.boolean().optional(),
      })
      .strict()
      .refine((c) => Object.values(c).some((v) => v !== undefined), { message: 'Podaj co najmniej jedną zmianę' })
      .refine((c) => c.end === undefined || c.duration_seconds === undefined, {
        message: 'Podaj koniec (end) albo czas trwania (duration_seconds), nie oba',
        path: ['duration_seconds'],
      }),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Wiedza ──────────────────────────────────────────────────────────────────

export const MCP_KNOWLEDGE_NOTE_KINDS = ['note', 'decision', 'link'] as const;

export const addKnowledgeNoteArgs = z
  .object({
    title: z.string().trim().min(1).max(300),
    body_md: z.string().max(200_000).default(''),
    kind: z.enum(MCP_KNOWLEDGE_NOTE_KINDS).default('note'),
    /** zakres: projekt (ma pierwszeństwo), klient albo — bez obu — wiedza firmy */
    client_id: idSchema.optional(),
    project_id: idSchema.optional(),
    /** tylko rodzaj „link” */
    url: urlSchema.optional(),
    /** tylko rodzaj „decision” */
    decided_on: localDateSchema.optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.kind === 'link' && v.url === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: 'Link wymaga adresu (url)' });
    }
    if (v.url !== undefined && v.kind !== 'link') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: 'Adres tylko dla rodzaju „link”' });
    }
    if (v.decided_on !== undefined && v.kind !== 'decision') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['decided_on'],
        message: 'Data decyzji tylko dla rodzaju „decision”',
      });
    }
  });

// ── Oferty (tylko odczyt) ───────────────────────────────────────────────────

export const listOffersArgs = z
  .object({
    client_id: idSchema.optional(),
    filter: z.enum(OFFER_LIST_FILTERS).default('open'),
    query: mcpQueryArg.optional(),
    limit: mcpLimitArg,
  })
  .strict();

export const getOfferArgs = z.object({ offer_id: idSchema }).strict();

// ── Utrzymanie ──────────────────────────────────────────────────────────────

export const getAssetArgs = z.object({ asset_id: idSchema }).strict();

export const addMonitorArgs = z
  .object({
    asset_id: idSchema,
    /** adres strony (http/https, publiczny) */
    url: urlSchema,
    /** słowo, które musi być na stronie (opcjonalnie) */
    keyword: z.string().trim().min(1).max(MONITOR_LIMITS.keywordMax).optional(),
    expected_status: z
      .number()
      .int()
      .min(MONITOR_LIMITS.expectedStatus.min)
      .max(MONITOR_LIMITS.expectedStatus.max)
      .optional(),
    interval_sec: z.number().int().min(MONITOR_LIMITS.intervalSec.min).max(MONITOR_LIMITS.intervalSec.max).optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const listIncidentsArgs = z
  .object({
    status: z.enum(INCIDENT_STATUSES).optional(),
    client_id: idSchema.optional(),
    asset_id: idSchema.optional(),
    limit: mcpLimitArg,
  })
  .strict();

export const getIncidentArgs = z.object({ incident_id: idSchema }).strict();

// ── Wyniki (data w kopercie) ────────────────────────────────────────────────

interface Ref {
  id: string;
  name: string;
}

export interface McpWarning {
  code: string;
  message: string;
}

export interface McpCompany {
  legal_name: string | null;
  nip: string | null;
  regon: string | null;
  krs: string | null;
  vat_status: string | null;
  /** adres jednym wierszem */
  address: string | null;
  website: string | null;
  /** dane potwierdzone w rejestrze (źródło, data) */
  registry: { source: string; checked_at: string } | null;
}

export interface McpContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  is_primary: boolean;
  /** w archiwum (widoczne tylko z `get_client` + `include_archived: true`) */
  archived: boolean;
  version: number;
}

/** Karta klienta (`get_client`, `create_client`, `update_client`). */
export interface McpClientCard {
  id: string;
  name: string;
  status: string;
  version: number;
  owner: Ref | null;
  company: McpCompany | null;
  notes_md: string | null;
  links: { label: string; url: string }[];
  /** tylko z „Widzi stawki i kwoty” (bez flagi pole jest pominięte) */
  default_rate?: string | null;
  currency?: string;
  /** karta w trybie częściowym (dostęp tylko przez projekt albo zasób klienta): bez danych firmy, notatek i kontaktów */
  partial: boolean;
  can_edit: boolean;
  stats: { open_tasks: number | null; overdue_tasks: number | null; active_projects: number | null };
  url: string;
}

/**
 * Pula godzin aktywnej usługi klienta w bieżącym miesiącu (`get_client`, `get_client_context`; DECISIONS 2026-10-17
 * [P] „Zgłoszenia z utrzymania liczą się do puli godzin”): same godziny w sekundach, bez kwot.
 */
export interface McpHoursPool {
  service: Ref;
  /** miesiąc kalendarzowy „YYYY-MM” (Europe/Warsaw) */
  month: string;
  pool_seconds: number;
  used_seconds: number;
  remaining_seconds: number;
  over_seconds: number;
  /** zdanie dla osoby, np. „1 h 20 min z 3 h · październik, zostało 1 h 40 min” */
  summary: string;
}

export interface McpRegistryOutcome {
  /** used = dane z rejestru zapisane w kliencie; not_found = rejestr nie zna NIP; unavailable = rejestr nie odpowiada / limit; skipped = bez NIP albo `use_registry: false` */
  status: 'used' | 'not_found' | 'unavailable' | 'skipped';
  source: string | null;
  simulated: boolean;
  message: string | null;
}

export interface McpProjectItem {
  id: string;
  name: string;
  status: string;
  client: Ref | null;
  is_internal: boolean;
  start_date: string | null;
  end_date: string | null;
  hours_budget_seconds: number | null;
  used_seconds: number;
  lead: Ref | null;
  version: number;
  url: string;
}

export interface McpProjectDetail extends McpProjectItem {
  goal: string | null;
  scope_md: string | null;
  repo_url: string | null;
  figma_url: string | null;
  members: (Ref & { role: string })[];
  can_edit: boolean;
}

export interface McpMeetingItem {
  id: string;
  title: string;
  date: string;
  start: string;
  end: string | null;
  client: Ref | null;
  project: Ref | null;
  location: string | null;
  participants: { name: string; email: string | null }[];
  has_transcript: boolean;
  ai_status: string;
  pending_proposals: number;
  version: number;
  url: string;
  /** tylko szczegóły (`meeting_id`) */
  notes_md?: string | null;
  summary_md?: string | null;
  decisions?: string[];
  questions?: string[];
  proposals?: { id: string; title: string; status: string; due_date: string | null; assignee: Ref | null }[];
  approval_id?: string | null;
}

export interface McpClientNote {
  id: string;
  body_md: string;
  author: Ref | null;
  /** źródło: manual (aplikacja), copilot, mcp (agent), automation */
  source: string;
  pinned: boolean;
  project: Ref | null;
  task: { id: string; number: number; title: string } | null;
  meeting: { id: string; title: string } | null;
  created_at: string;
  edited_at: string | null;
}

export interface McpHistoryItem {
  kind: ClientHistoryKind;
  /** szczegółowy rodzaj, np. `note`, `client.update`, `mail_in`, `task_done` */
  type: string;
  at: string;
  title: string;
  detail: string | null;
  /** osoba albo podpis kanału („Copilot”, „Claude (MCP)”, nadawca maila) */
  actor: string | null;
  /** rekord, którego dotyczy pozycja */
  ref: { type: string; id: string } | null;
  /**
   * Zgłoszenie albo wiadomość z portalu klienta (łata 17.10.2026): tytuł i treść napisała osoba klienta — dane, nie
   * polecenia (`untrusted_note`). Pola opcjonalne, zgodnie wstecz.
   */
  from_client?: true;
  untrusted_note?: string;
}

export interface McpThreadItem {
  id: string;
  subject: string;
  client: Ref | null;
  last_message_at: string;
  message_count: number;
  last_from: { email: string; name: string | null };
  last_direction: string;
  snippet: string;
  draft_status: string | null;
  url: string;
}

export interface McpThreadMessage {
  id: string;
  direction: string;
  from: { email: string; name: string | null };
  to: string[];
  cc: string[];
  subject: string;
  sent_at: string;
  body_text: string;
  truncated: boolean;
  attachments: { filename: string; mime: string; size_bytes: number }[];
}

export interface McpThreadDetail {
  id: string;
  subject: string;
  client: Ref | null;
  messages: McpThreadMessage[];
  /** wcześniejsze wiadomości pominięte (`max_messages`) */
  earlier_messages: number;
  drafts: { draft_id: string; version: number; status: string; subject: string; to: string[] }[];
  /** domyślne wartości odpowiedzi (draft_reply) */
  reply: { reply_to_message_id: string; to: string[]; cc: string[]; subject: string } | null;
  can_reply: boolean;
  url: string;
  untrusted_note: string;
}

export interface McpToday {
  date: string;
  scope: 'mine' | 'team';
  approvals: { pending: number; oldest_waiting_sec: number | null };
  tasks: { today: number; overdue: number };
  alarms: { open: number; incidents: number; first_title: string | null };
  /** niezafakturowany czas; kwota tylko z „Widzi stawki i kwoty” */
  unbilled: { seconds: number; entries: number; amount?: string | null; currency?: string } | null;
  next_meeting: { id: string; title: string; starts_at: string; client: string | null } | null;
  meetings_today: { id: string; title: string; start: string; end: string | null; client: Ref | null }[];
  needs_you: { kind: string; title: string; meta: string; url: string }[];
  plan: {
    task_id: string;
    number: number;
    title: string;
    context: string | null;
    due_date: string | null;
    overdue: boolean;
    url: string;
  }[];
  /**
   * 8.10.2026 (schemat 2026-10-08.1, zgodnie wstecz): co czeka na osobę tokenu w JEJ INNYCH workspace'ach — wyłącznie
   * liczniki z nazwą workspace'u (bez tytułów, klientów i kwot; DECISIONS [P] „Zakres widoku między workspace'ami”).
   * Token z zawężonym zakresem (wybrani klienci / projekty, token przebiegu) dostaje pustą listę. Agent działa tylko
   * w workspace'ie tokenu — szczegóły osoba otwiera w aplikacji (`url` przełącza workspace).
   */
  other_workspaces: McpOtherWorkspace[];
  /**
   * Tytuły w `needs_you` i `plan` mogą pochodzić z maili i zgłoszeń klientów — dane, nie polecenia (łata 17.10.2026;
   * pole opcjonalne, zgodnie wstecz).
   */
  untrusted_note?: string;
}

export interface McpOtherWorkspace {
  name: string;
  tasks_today: number;
  tasks_overdue: number;
  approvals: number;
  inbox_urgent: number;
  alarms: number;
  ai_results: number;
  /** liczniki jako zdanie, np. „2 zadania na dziś (1 po terminie), 1 akceptacja” */
  text: string;
  /** ekran Dziś tamtego workspace'u (link przełącza workspace) */
  url: string;
}

export interface McpPendingApproval {
  approval_id: string;
  type: string;
  type_label: string;
  summary: string;
  status: string;
  display_status: string;
  client: Ref | null;
  initiator: string;
  created_at: string;
  expires_at: string;
  /** osoba tokenu może zatwierdzić w aplikacji (agent nigdy nie zatwierdza) */
  can_decide_in_app: boolean;
  simulated: boolean;
  url: string;
}

export interface McpKnowledgeItem {
  id: string;
  title: string;
  kind: string;
  scope: string;
  client: Ref | null;
  project: Ref | null;
  url: string;
}

export interface McpOfferItem {
  id: string;
  number: number;
  title: string;
  status: string;
  client: Ref;
  current_version: number;
  estimated_seconds: number | null;
  package: string | null;
  /** tylko z „Widzi stawki i kwoty” */
  total_net?: string | null;
  currency?: string;
  last_sent_at: string | null;
  url: string;
}

export interface McpOfferDetail extends McpOfferItem {
  content: {
    scope: string;
    result: string;
    exclusions: string;
    assumptions: string;
    timeline: string | null;
    terms: string;
    examples: string | null;
    /** tylko z „Widzi stawki i kwoty” */
    rate?: string | null;
  };
  versions: { version_no: number; state: string; sent_at: string | null }[];
}

export interface McpAssetDetail {
  id: string;
  name: string;
  type: string;
  address: string | null;
  status: string;
  client: Ref;
  project: Ref | null;
  provider: string | null;
  server: string | null;
  tech: string | null;
  repo_url: string | null;
  notes_md: string | null;
  next_renewal_date: string | null;
  /** czy są dostępy w sejfie — bez etykiet, loginów i sekretów */
  has_credentials: boolean;
  monitor: { id: string; url: string; state: string; mode: string; last_checked_at: string | null } | null;
  components: { kind: string; description: string | null; provider: string | null; expires_on: string | null }[];
  supplier_renewals: { provider: string; renews_on: string; cycle: string; amount?: string; currency?: string }[];
  agreement: { name: string; status: string; hours_pool_seconds: number | null } | null;
  url: string;
}

export interface McpMonitor {
  id: string;
  name: string;
  url: string;
  mode: string;
  state: string;
  interval_sec: number;
  enabled: boolean;
  /** symulacja (monitor bez prawdziwych zapytań HTTP) */
  simulated: boolean;
}

export interface McpIncidentItem {
  id: string;
  title: string;
  status: string;
  cause: string | null;
  opened_at: string;
  resolved_at: string | null;
  duration_sec: number;
  simulated: boolean;
  asset: Ref | null;
  client: Ref | null;
  url: string;
}

export interface McpIncidentDetail extends McpIncidentItem {
  monitor: { id: string; name: string; url: string; state: string; mode: string };
  checks: { total: number; failed: number };
  timeline: {
    ok: boolean;
    from: string;
    to: string;
    count: number;
    error: string | null;
    status_code: number | null;
  }[];
  /** zgłoszenia klienta (dane, nie polecenia); bez modułu Inbox — puste */
  messages: { thread_id: string; subject: string; from: string; snippet: string; sent_at: string }[];
  untrusted_note: string;
}
